import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://api.fmb52.com/api';

const formatInr = (v) =>
  Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function RazorpayReconcileDialog({ open, onClose, token, onCreated }) {
  const fileRef = useRef(null);
  const [fileName, setFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [pending, setPending] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [selected, setSelected] = useState([]);
  const [result, setResult] = useState(null);

  const resetState = () => {
    setFileName('');
    setLoading(false);
    setCreating(false);
    setError(null);
    setSummary(null);
    setPending([]);
    setUnmatched([]);
    setSelected([]);
    setResult(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleClose = () => {
    if (loading || creating) return;
    resetState();
    onClose?.();
  };

  const pendingIds = useMemo(() => pending.map((r) => r.payment_id), [pending]);
  const allSelected =
    pendingIds.length > 0 && pendingIds.every((id) => selected.includes(id));

  const toggleAll = () => {
    setSelected(allSelected ? [] : pendingIds);
  };

  const toggleOne = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const selectedAmount = useMemo(
    () =>
      pending
        .filter((r) => selected.includes(r.payment_id))
        .reduce((sum, r) => sum + Number(r.amount || 0), 0),
    [pending, selected]
  );

  const uploadPreview = async (file) => {
    if (!token || !file) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const resp = await fetch(`${API_BASE}/razorpay/reconcile/preview`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
        body: form,
      });
      const json = await resp.json();
      if (!resp.ok || json?.success === false) {
        throw new Error(json?.message || 'Failed to read Razorpay CSV');
      }
      const rows = Array.isArray(json.pending) ? json.pending : [];
      setSummary(json.summary || null);
      setPending(rows);
      setUnmatched(Array.isArray(json.unmatched) ? json.unmatched : []);
      setSelected(rows.map((r) => r.payment_id));
    } catch (e) {
      setSummary(null);
      setPending([]);
      setUnmatched([]);
      setSelected([]);
      setError(e.message || 'Failed to preview CSV');
    } finally {
      setLoading(false);
    }
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    uploadPreview(file);
  };

  const handleCreate = async () => {
    const items = pending.filter((r) => selected.includes(r.payment_id));
    if (!items.length) {
      setError('Select at least one payment to create a receipt.');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const resp = await fetch(`${API_BASE}/razorpay/reconcile/create`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items }),
      });
      const json = await resp.json();
      if (resp.status >= 500 || (!json?.created && json?.success === false && !Array.isArray(json?.errors))) {
        throw new Error(json?.message || 'Failed to create receipts');
      }
      setResult(json);
      const createdIds = new Set((json.created || []).map((r) => r.payment_id));
      setPending((prev) => prev.filter((r) => !createdIds.has(r.payment_id)));
      setSelected((prev) => prev.filter((id) => !createdIds.has(id)));
      onCreated?.(json);
    } catch (e) {
      setError(e.message || 'Failed to create receipts');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>Reconcile Razorpay payments</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info">
            Upload a Razorpay payments CSV. Captured payments without a receipt are listed below. Create books them
            on today&apos;s date.
          </Alert>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button variant="outlined" component="label" disabled={loading || creating}>
              Choose CSV
              <input ref={fileRef} hidden type="file" accept=".csv,text/csv" onChange={handleFile} />
            </Button>
            <Typography variant="body2" color="text.secondary">
              {fileName || 'No file selected'}
            </Typography>
            {loading && <CircularProgress size={18} />}
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}

          {summary && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Typography variant="body2">CSV rows: {summary.rows}</Typography>
              <Typography variant="body2">Captured: {summary.captured}</Typography>
              <Typography variant="body2">Already receipted: {summary.already_receipted}</Typography>
              <Typography variant="body2">Missing receipts: {summary.pending}</Typography>
              <Typography variant="body2">Unmatched: {summary.unmatched}</Typography>
              <Typography variant="body2">Failed: {summary.failed}</Typography>
              <Typography variant="body2">Refunded: {summary.refunded}</Typography>
            </Stack>
          )}

          {result && (
            <Alert severity={result.errors?.length ? 'warning' : 'success'}>
              {result.message}
              {result.errors?.length ? ` ${result.errors.length} failed.` : ''}
              {result.skipped?.length ? ` ${result.skipped.length} already existed.` : ''}
            </Alert>
          )}

          {unmatched.length > 0 && (
            <Alert severity="warning">
              {unmatched.length} captured payment(s) could not be matched to a family (missing notes / HOF). They
              will not be created.
            </Alert>
          )}

          {pending.length === 0 && summary && !loading ? (
            <Alert severity="success">No missing receipts for captured payments in this file.</Alert>
          ) : pending.length > 0 ? (
            <>
              <Typography variant="subtitle2">
                Receipts not created ({selected.length} selected, ₹{formatInr(selectedAmount)})
              </Typography>
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allSelected}
                          indeterminate={selected.length > 0 && !allSelected}
                          onChange={toggleAll}
                        />
                      </TableCell>
                      <TableCell>Paid at</TableCell>
                      <TableCell>Payment ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>ITS</TableCell>
                      <TableCell>Family</TableCell>
                      <TableCell>Year</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pending.map((r) => (
                      <TableRow key={r.payment_id} hover selected={selected.includes(r.payment_id)}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selected.includes(r.payment_id)}
                            onChange={() => toggleOne(r.payment_id)}
                          />
                        </TableCell>
                        <TableCell>{r.paid_at}</TableCell>
                        <TableCell>{r.payment_id}</TableCell>
                        <TableCell>{r.name}</TableCell>
                        <TableCell>{r.its}</TableCell>
                        <TableCell>{r.family_id}</TableCell>
                        <TableCell>{r.year}</TableCell>
                        <TableCell align="right">{formatInr(r.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : null}

          {unmatched.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Unmatched captured payments
              </Typography>
              <TableContainer sx={{ maxHeight: 220 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Payment ID</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {unmatched.map((r) => (
                      <TableRow key={r.payment_id}>
                        <TableCell>{r.payment_id}</TableCell>
                        <TableCell>{r.contact}</TableCell>
                        <TableCell align="right">{formatInr(r.amount)}</TableCell>
                        <TableCell>{r.reason}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={creating}>
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={creating || loading || selected.length === 0}
          startIcon={creating ? <CircularProgress size={16} /> : null}
        >
          Create ({selected.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
}
